import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import { collectMediaAssetIdsFromUploadMedias } from "../../lib/utils/mediaAssetId"

/** Collect every mediaAsset.id from upload API payload (`medias[].mediaAssets[]`). */
export function collectMediaAssetIdsFromUploadResult(result, options = {}) {
  const fileCount = options.fileCount ?? options.expectedCount ?? null
  return collectMediaAssetIdsFromUploadMedias(result?.medias, fileCount)
}

const IN_PROGRESS_STATUSES = new Set(["ANALYZING", "PENDING", "IN_PROGRESS"])

function isAnalysisStatusInProgress(status) {
  const u = String(status ?? "").trim().toUpperCase()
  if (!u) return false
  return IN_PROGRESS_STATUSES.has(u)
}

/**
 * GET `media/analysis/status` → `{ [mediaAssetId]: status }`.
 * - Plain string body (single-id query): status string, keyed by the requested id.
 * - Array or single object: `mediaAssetId` + `status` on each row.
 */
export function extractMediaAssetStatusMap(raw, requestedMediaAssetIds = null) {
  const map = {}
  if (raw == null) return map

  const requested = Array.isArray(requestedMediaAssetIds)
    ? requestedMediaAssetIds.filter((id) => id != null)
    : []

  if (typeof raw === "string") {
    const t = raw.trim()
    if (t && requested.length === 1) {
      map[requested[0]] = t
    }
    return map
  }

  const takeRow = (row) => {
    if (!row || typeof row !== "object") return
    const id = row.mediaAssetId
    const st = row.status
    if (id != null && st != null) map[id] = String(st)
  }

  if (Array.isArray(raw)) {
    raw.forEach(takeRow)
    return map
  }

  if (typeof raw === "object") {
    takeRow(raw)
  }

  return map
}

/** True when no asset in the response is still analyzing. */
export function isMediaAnalysisStatusComplete(raw) {
  if (raw == null) return true

  if (typeof raw === "string") {
    return !isAnalysisStatusInProgress(raw)
  }

  if (Array.isArray(raw)) {
    if (raw.length === 0) return true
    return raw.every((item) => !isAnalysisStatusInProgress(item?.status))
  }

  if (typeof raw === "object") {
    if (typeof raw.status === "string") {
      return !isAnalysisStatusInProgress(raw.status)
    }
    return true
  }

  return true
}

export const fetchMediaAnalysisStatus = createAsyncThunk(
  "mediaAnalysisStatus/fetch",
  async (mediaAssetIds, { rejectWithValue }) => {
    try {
      if (!Array.isArray(mediaAssetIds) || mediaAssetIds.length === 0) {
        return null
      }
      const params = new URLSearchParams()
      mediaAssetIds.forEach((id) => {
        params.append("mediaAssetIds", id)
      })
      const response = await get(`media/analysis/status?${params.toString()}`)
      return response
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch media analysis status")
    }
  },
)

const mediaAnalysisStatusSlice = createSlice({
  name: "mediaAnalysisStatus",
  initialState: {
    status: "idle",
    lastResponse: null,
    error: null,
  },
  reducers: {
    clearMediaAnalysisStatus: (state) => {
      state.status = "idle"
      state.lastResponse = null
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMediaAnalysisStatus.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchMediaAnalysisStatus.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.lastResponse = action.payload
      })
      .addCase(fetchMediaAnalysisStatus.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
  },
})

export const { clearMediaAnalysisStatus } = mediaAnalysisStatusSlice.actions
export default mediaAnalysisStatusSlice.reducer

const POLL_INTERVAL_MS = 20_000
const MAX_POLL_ATTEMPTS = 120

let activeAnalysisPollToken = 0

/** Cancel in-flight pollMediaAnalysisUntilComplete loops (e.g. new upload started). */
export function cancelMediaAnalysisPolling() {
  activeAnalysisPollToken += 1
  return activeAnalysisPollToken
}

/** Poll analysis after POST `media/clients/:id` using `medias[].mediaAssets[].id`. */
export async function pollUploadResultAnalysisUntilComplete(dispatch, uploadResult, options = {}) {
  const fileCount = options.fileCount ?? options.expectedCount ?? null
  const ids = collectMediaAssetIdsFromUploadResult(uploadResult, { fileCount })
  return pollMediaAnalysisUntilComplete(dispatch, ids, options)
}

export async function pollMediaAnalysisUntilComplete(dispatch, mediaAssetIds, options = {}) {
  const ids = Array.isArray(mediaAssetIds)
    ? [...new Set(mediaAssetIds.filter((id) => id != null && String(id).trim() !== ""))]
    : []
  if (ids.length === 0) return true

  const pollToken = options.pollToken ?? activeAnalysisPollToken
  const isCancelled =
    typeof options.isCancelled === "function"
      ? options.isCancelled
      : () => pollToken !== activeAnalysisPollToken

  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    if (isCancelled()) return false
    try {
      const res = await dispatch(fetchMediaAnalysisStatus(ids)).unwrap()
      if (isMediaAnalysisStatusComplete(res)) return true
    } catch {
      // network/API error — retry on next interval
    }
    if (attempt < MAX_POLL_ATTEMPTS - 1) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
    }
  }
  return false
}
